import * as React from "react";
import {
  Link,
  Box,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  InputBase,
  Button,
  Stack,
  Select,
  MenuItem,
  Pagination,
} from "@mui/material";
import moment from "moment";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import SearchIcon from "@mui/icons-material/Search";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import FilterListIcon from "@mui/icons-material/FilterList";
import ArrowDropUp from "@mui/icons-material/ArrowDropUp";
import { AuthenticationContext } from "../Context";
import { PrivilegesContext } from "../PrivilegesContext";
import { getHeaders } from "../utils/HeaderHelper";
import DatePickerWithLabel from "../common/DatePickerWithLabel";
import LoadingReportsTable from "../shared/LoadingReportsTable";
import EmptyReportsTable from "../shared/EmptyReportsTable";
import ReportAdvanceFilters from "./ReportAdvancedFilters";
import BadgeList from "../common/BadgeList";
import { checkPrivileges } from "../utils/PrivilegesHelper";

interface Props {}

interface ChipData {
  key: number;
  label: string;
  type: string;
}

const OneProgramReport: React.FC<Props> = (props: any) => {
  const authenticationSetting = React.useContext(AuthenticationContext);
  const userPrivileges = React.useContext(PrivilegesContext);

  const debounceTimeout = React.useRef<NodeJS.Timeout | null>(null);
  const fetchSeqRef = React.useRef(0);
  const [customerOptions, setCustomerOptions] = React.useState<any>();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [startDate, setStartDate] = React.useState<string>(null);
  const [endDate, setEndDate] = React.useState<string>(
    moment().format("YYYY-MM-DD")
  );
  const [lastName, setLastName] = React.useState<string>("");
  const [debouncedLastName, setDebouncedLastName] = React.useState<string>("");
  const [selectedOrganizations, setSelectedOrganizations] = React.useState<
    any[]
  >([]);
  const [badgeData, setBadgeData] = React.useState<ChipData[]>([]);
  const [tableData, setTableData] = React.useState<any>([]);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [currentPage, setCurrentPage] = React.useState<number>(1);
  const [pageCount, setPageCount] = React.useState<number>(0);
  const [totalCount, setTotalCount] = React.useState<number>(0);
  const [perPage, setPerPage] = React.useState<number>(100);

  React.useEffect(() => {
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    debounceTimeout.current = setTimeout(() => {
      setDebouncedLastName(lastName);
    }, 500);

    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
  }, [lastName]);

  // Reset page before paint when filter inputs change so fetch effect sees the correct page once.
  React.useLayoutEffect(() => {
    setCurrentPage(1);
  }, [startDate, endDate, selectedOrganizations, debouncedLastName, perPage]);

  React.useEffect(() => {
    const seq = ++fetchSeqRef.current;
    const controller = new AbortController();

    setLoading(true);

    const params = new URLSearchParams({
      start_date: startDate || "",
      end_date: endDate || "",
      last_name: debouncedLastName.length > 2 ? debouncedLastName : "",
      selected_organizations: selectedOrganizations?.join(",") || "",
      page: currentPage.toString(),
      per_page: perPage.toString(),
    });

    fetch(`/admin/reports/one_program/report_data?${params}`, {
      method: "GET",
      headers: getHeaders(authenticationSetting.csrfToken),
      signal: controller.signal,
    })
      .then((result) => result.json())
      .then((result) => {
        if (seq !== fetchSeqRef.current) return;
        if (!result.success) {
          console.log(result.error);
          setTableData([]);
          setPageCount(0);
          setTotalCount(0);
          return;
        }
        setTableData(result?.resource?.results || []);
        setPageCount(result?.resource?.total_pages || 0);
        setTotalCount(result?.resource?.total_count || 0);
      })
      .catch((error) => {
        if ((error as Error).name === "AbortError") return;
        console.error("Failed to fetch report data:", error);
        if (seq === fetchSeqRef.current) {
          setTableData([]);
          setPageCount(0);
          setTotalCount(0);
        }
      })
      .finally(() => {
        if (seq === fetchSeqRef.current) setLoading(false);
      });

    return () => controller.abort();
  }, [
    startDate,
    endDate,
    selectedOrganizations,
    debouncedLastName,
    currentPage,
    perPage,
    authenticationSetting.csrfToken,
  ]);

  React.useEffect(() => {
    getAssets();
  }, []);

  const getAssets = () => {
    fetch(`/admin/reports/one_program/assets`, {
      method: "GET",
      headers: getHeaders(authenticationSetting.csrfToken),
    })
      .then((result) => result.json())
      .then((result) => {
        if (result.success) {
          setCustomerOptions(result?.resource?.customers);
        } else {
          console.log(result.error);
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const selectPage = (page) => {
    if (page < 1 || page > pageCount) return;
    setCurrentPage(page);
  };

  const handleOpenFilter = (event: React.MouseEvent<HTMLElement>) => {
    if (loading) return;
    setAnchorEl(event.currentTarget);
  };

  const handleDeleteBadge = (badgeToDelete: ChipData) => {
    setSelectedOrganizations((prevSelected) =>
      prevSelected.filter((id) => id !== badgeToDelete.key)
    );
  };

  React.useEffect(() => {
    const orgIdToNameMap = customerOptions?.reduce((acc, org) => {
      acc[org.id] = org.name;
      return acc;
    }, {} as Record<string | number, string>);

    const badges = selectedOrganizations
      .map((orgId) => ({
        key: orgId,
        label: orgIdToNameMap?.[orgId] ?? `Organization ${orgId}`,
        type: "organization",
      }))
      .filter((badge) => badge.label);
    setBadgeData(badges);
  }, [selectedOrganizations, customerOptions]);

  return (
    <Box className="report-main-content">
      <Box className="main-header">
        <Box className="heading-with-back-button-section">
          <Stack direction="row" columnGap="12px">
            <Link
              style={{
                textDecoration: "none",
                display: "flex",
                alignItems: "center",
              }}
              href={`/reports`}
            >
              <ArrowBackIosNewIcon
                style={{
                  borderRadius: "3px",
                  padding: "8px",
                  color: "black",
                  boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.2)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  fontSize: "14px",
                }}
              />
            </Link>
            <Typography className="main-heading">ONE Program Report</Typography>
          </Stack>
        </Box>
      </Box>

      <Box className="report-details">
        <Box className="sub-header">
          <Box className="subheading-with-pill-section">
            <Typography className="sub-heading">Report Details</Typography>
            <Box className="count-pill">{totalCount}</Box>
          </Box>

          <Box className="filters-section">
            <Paper className="input-paper">
              <InputBase
                placeholder="Search Last Name"
                inputProps={{ "aria-label": "search last name" }}
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                disabled={loading}
              />
              <SearchIcon sx={{ color: "#707070" }} />
            </Paper>
            <DatePickerWithLabel
              date={startDate}
              setDate={setStartDate}
              initialDate={moment().subtract(1, "month").format("YYYY-MM-DD")}
              label="Encounter Start Date"
              disabled={loading}
            />
            <DatePickerWithLabel
              date={endDate}
              setDate={setEndDate}
              initialDate={moment().format("YYYY-MM-DD")}
              label="Encounter End Date"
              disabled={loading}
            />
            {checkPrivileges(
              userPrivileges,
              "Globalize ONE Program Reports"
            ) && (
              <div
                className="advanced-search-container"
                onClick={loading ? undefined : handleOpenFilter}
                style={{
                  cursor: loading ? "not-allowed" : "pointer",
                  opacity: loading ? 0.8 : 1,
                }}
              >
                <FilterListIcon sx={{ width: 20, height: 20 }} />
                <p className="advanced-search-text">Advanced Search</p>
                {anchorEl ? (
                  <ArrowDropUp sx={{ width: 24, height: 24 }} />
                ) : (
                  <ArrowDropDownIcon sx={{ width: 24, height: 24 }} />
                )}
                <ReportAdvanceFilters
                  anchorEl={anchorEl}
                  setAnchorEl={setAnchorEl}
                  organizationOptions={customerOptions}
                  selectedOrganizations={selectedOrganizations}
                  setSelectedOrganizations={setSelectedOrganizations}
                  disabled={loading}
                />
              </div>
            )}
            {/* <Button
              className="export-csv-button"
              onClick={() => {
                const params = new URLSearchParams({
                  start_date: startDate || "",
                  end_date: endDate || "",
                  last_name: lastName || "",
                  selected_organizations:
                    selectedOrganizations?.join(",") || "",
                }).toString();

                window.location.href = `/admin/reports/one_program/generate_csv.csv?${params}`;
              }}
            >
              Export as CSV
            </Button> */}
          </Box>
        </Box>

        <Box className="table-section">
          {badgeData?.length > 0 && (
            <div
              className="badge-container"
              style={{ width: "auto", marginBottom: "20px" }}
            >
              <BadgeList
                badgeData={badgeData}
                setBadgeData={setBadgeData}
                handleDeleteBadge={handleDeleteBadge}
                background="none"
              />
            </div>
          )}
          {loading ? (
            <LoadingReportsTable isBadgeShowing={badgeData.length > 0} />
          ) : (
            <>
              {tableData?.length > 0 ? (
                <TableContainer
                  className="report-table-container"
                  style={{ height: badgeData.length > 0 ? "45vh" : "53.8vh" }}
                >
                  <Table stickyHeader>
                    <TableHead className="table-header">
                      <TableRow
                        sx={{
                          "& td, & th": {
                            borderBottom: "none",
                          },
                        }}
                      >
                        <TableCell align="left" sx={{ minWidth: "170px" }}>
                          Organization Name
                        </TableCell>
                        <TableCell align="left" sx={{ minWidth: "120px" }}>
                          Organization NPI
                        </TableCell>
                        <TableCell align="left" sx={{ minWidth: "130px" }}>
                          Organization City
                        </TableCell>
                        <TableCell align="left" sx={{ minWidth: "130px" }}>
                          Organization State
                        </TableCell>
                        <TableCell align="left" sx={{ minWidth: "130px" }}>
                          Organization Zip
                        </TableCell>
                        <TableCell align="left" sx={{ minWidth: "146px" }}>
                          Patient First Name
                        </TableCell>
                        <TableCell align="left" sx={{ minWidth: "146px" }}>
                          Patient Last Name
                        </TableCell>
                        <TableCell align="left" sx={{ minWidth: "125px" }}>
                          Patient MIddle Initial
                        </TableCell>
                        <TableCell align="left" sx={{ minWidth: "110px" }}>
                          Encounter Date
                        </TableCell>
                        <TableCell align="left" sx={{ minWidth: "110px" }}>
                          Patient MRN #
                        </TableCell>
                        <TableCell align="left" sx={{ minWidth: "110px" }}>
                          Patient DOB
                        </TableCell>
                        <TableCell align="left" sx={{ minWidth: "94px" }}>
                          Patient Age
                        </TableCell>
                        <TableCell align="left" sx={{ minWidth: "110px" }}>
                          Patient Gender
                        </TableCell>
                        <TableCell align="left" sx={{ minWidth: "150px" }}>
                          Submitted By
                        </TableCell>
                        <TableCell align="left" sx={{ minWidth: "210px" }}>
                          What is the patient's birth gender?
                        </TableCell>
                        <TableCell align="left" sx={{ minWidth: "210px" }}>
                          What is the patient's age?
                        </TableCell>
                        <TableCell align="left" sx={{ minWidth: "1008px" }}>
                          Has the patient taken this or other opioid medications
                          in the last 60 days? Examples: Duragesic(R)
                          (fentanyl), Oxycontin(R) (oxycodone), Vicodin(R)
                          (hydrocodone), morphine
                        </TableCell>
                        <TableCell align="left" sx={{ minWidth: "425px" }}>
                          Does the patient have a family history of substance
                          abuse with alcohol?
                        </TableCell>
                        <TableCell align="left" sx={{ minWidth: "455px" }}>
                          Does the patient have a family history of substance
                          abuse with illegal drugs?
                        </TableCell>
                        <TableCell align="left" sx={{ minWidth: "528px" }}>
                          Does the patient have a family history of substance
                          abuse with prescription medications?
                        </TableCell>
                        <TableCell align="left" sx={{ minWidth: "440px" }}>
                          Does the patient have a personal history of substance
                          abuse with alcohol?
                        </TableCell>
                        <TableCell align="left" sx={{ minWidth: "470px" }}>
                          Does the patient have a personal history of substance
                          abuse with illegal drugs?
                        </TableCell>
                        <TableCell align="left" sx={{ minWidth: "542px" }}>
                          Does the patient have a personal history of substance
                          abuse with prescription medications?
                        </TableCell>
                        <TableCell align="left" sx={{ minWidth: "258px" }}>
                          Is the patient's age between 16 - 45 years?
                        </TableCell>
                        <TableCell align="left" sx={{ minWidth: "378px" }}>
                          Does the patient have a history of preadolescent
                          sexual abuse?
                        </TableCell>
                        <TableCell align="left" sx={{ minWidth: "777px" }}>
                          Does the patient have attention deficit disorder
                          (ADD), obsessive compulsive disorder (OCD), bipolar
                          disorder, and/or schizophrenia?
                        </TableCell>
                        <TableCell align="left" sx={{ minWidth: "190px" }}>
                          Does Patient Have Depression?
                        </TableCell>
                        <TableCell align="left" sx={{ minWidth: "280px" }}>
                          Does the patient have COPD/emphysema?
                        </TableCell>
                        <TableCell align="left" sx={{ minWidth: "240px" }}>
                          Does the patient have sleep apnea?
                        </TableCell>
                        <TableCell align="left" sx={{ minWidth: "420px" }}>
                          Does the patient have liver disease and/or kidney
                          disease?
                        </TableCell>
                        <TableCell align="left" sx={{ minWidth: "1364px" }}>
                          While using this medication is there a chance the
                          patient may consume: Medication used to treat anxiety
                          called a benzodiazepine. Examples: Xanax(R)
                          (alprazolam), Ativan(R) (lorazepam),Valium(R)
                          (diazepam) Klonopin(R)(clonazepam)
                        </TableCell>
                        <TableCell align="left" sx={{ minWidth: "983px" }}>
                          While using this medication is there a chance the
                          patient may consume: Medication used to treat nerve
                          pain Examples: Neurontin(R) (gabapentin), Lyrica(R)
                          (pregabalin)
                        </TableCell>
                        <TableCell align="left" sx={{ minWidth: "1166px" }}>
                          While using this medication is there a chance the
                          patient may consume: Medication known as a muscle
                          relaxer Examples: Flexeril(R) (cyclobenzaprine),
                          Skelaxin(R) (metaxalone), Soma(R) (carisoprodol)
                        </TableCell>
                        <TableCell align="left" sx={{ minWidth: "800px" }}>
                          While using this medication is there a chance the
                          patient may consume: Medication used to aid in sleep
                          (prescription or over the counter)
                        </TableCell>
                        <TableCell align="left" sx={{ minWidth: "468px" }}>
                          While using this medication is there a chance the
                          patient may consume: Alcohol
                        </TableCell>
                        <TableCell align="left" sx={{ minWidth: "1296px" }}>
                          While using this medication is there a chance the
                          patient may consume, or is the patient currently
                          taking other opioid medication(s)? Examples: Duragesic
                          (fentanyl), Oxycontin (oxycodone), Vicodin
                          (hydrocodone), morphine
                        </TableCell>
                        <TableCell align="left" sx={{ minWidth: "1028px" }}>
                          OPIOID MISUSE RISK ASSESSMENT: Today, the patient was
                          identified at high risk for future opioid misuse (ORT
                          ≥7) If yes, the risk of opioid misuse was discussed
                          with the patient
                        </TableCell>
                        <TableCell align="left" sx={{ minWidth: "976px" }}>
                          ACCIDENTAL OVERDOSE RISK ASSESSMENT: Today, the
                          patient was identified as at risk for accidental
                          overdose based on current disease states or current
                          medications
                        </TableCell>
                        <TableCell align="left" sx={{ minWidth: "376px" }}>
                          A medication take-back program was introduced to the
                          patient
                        </TableCell>
                        <TableCell align="left" sx={{ minWidth: "253px" }}>
                          The opioid prescription was partially filled
                        </TableCell>
                        <TableCell align="left" sx={{ minWidth: "290px" }}>
                          Opioid use disorder was explained to the patient
                        </TableCell>
                        <TableCell align="left" sx={{ minWidth: "493px" }}>
                          Community support/treatment services information was
                          discussed with the patient
                        </TableCell>
                        <TableCell align="left" sx={{ minWidth: "244px" }}>
                          The benefits of naloxone were explained
                        </TableCell>
                        <TableCell align="left" sx={{ minWidth: "294px" }}>
                          Pharmacist identified the patient needs naloxone
                        </TableCell>
                        <TableCell align="left" sx={{ minWidth: "371px" }}>
                          Patient accepted naloxone to take home (dispensed
                          naloxone)
                        </TableCell>
                        <TableCell align="left" sx={{ minWidth: "326px" }}>
                          The prescriber of the opioid medication was contacted
                        </TableCell>
                        <TableCell align="left" sx={{ minWidth: "1237px" }}>
                          Pharmacist identified the patient is using multiple
                          providers for opioid prescriptions, and patient was
                          provided consultation regarding overdose-risk in
                          individuals that use multiple providers for opioid
                          medications.
                        </TableCell>
                        <TableCell align="left" sx={{ minWidth: "168px" }}>
                          Please Enter the ORT Score
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {tableData?.map((report) => (
                        <TableRow
                          sx={{ "& td, & th": { borderBottom: "none" } }}
                          key={report.id}
                        >
                          <TableCell align="left">
                            {report.customer_name}
                          </TableCell>
                          <TableCell align="left">
                            {report.organization_npi}
                          </TableCell>
                          <TableCell align="left">
                            {report.organization_city}
                          </TableCell>
                          <TableCell align="left">
                            {report.organization_state}
                          </TableCell>
                          <TableCell align="left">
                            {report.organization_zip}
                          </TableCell>
                          <TableCell align="left">
                            {report.first_name}
                          </TableCell>
                          <TableCell align="left">{report.last_name}</TableCell>
                          <TableCell align="left">
                            {report.middle_name}
                          </TableCell>
                          <TableCell align="left">
                            {report.encounter_date}
                          </TableCell>
                          <TableCell align="left">
                            {report.mrn_number}
                          </TableCell>
                          <TableCell align="left">{report.dob}</TableCell>
                          <TableCell align="left">{report.age}</TableCell>
                          <TableCell align="left">{report.sex}</TableCell>
                          <TableCell align="left">
                            {report.submitted_by || "System"}
                          </TableCell>
                          <TableCell align="left">{report.answer_1}</TableCell>
                          <TableCell align="left">{report.answer_2}</TableCell>
                          <TableCell align="left">{report.answer_3}</TableCell>
                          <TableCell align="left">{report.answer_4}</TableCell>
                          <TableCell align="left">{report.answer_5}</TableCell>
                          <TableCell align="left">{report.answer_6}</TableCell>
                          <TableCell align="left">{report.answer_7}</TableCell>
                          <TableCell align="left">{report.answer_8}</TableCell>
                          <TableCell align="left">{report.answer_9}</TableCell>
                          <TableCell align="left">{report.answer_10}</TableCell>
                          <TableCell align="left">{report.answer_11}</TableCell>
                          <TableCell align="left">{report.answer_12}</TableCell>
                          <TableCell align="left">{report.answer_13}</TableCell>
                          <TableCell align="left">{report.answer_14}</TableCell>
                          <TableCell align="left">{report.answer_15}</TableCell>
                          <TableCell align="left">{report.answer_16}</TableCell>
                          <TableCell align="left">{report.answer_17}</TableCell>
                          <TableCell align="left">{report.answer_18}</TableCell>
                          <TableCell align="left">{report.answer_19}</TableCell>
                          <TableCell align="left">{report.answer_20}</TableCell>
                          <TableCell align="left">{report.answer_21}</TableCell>
                          <TableCell align="left">{report.answer_22}</TableCell>
                          <TableCell align="left">{report.answer_23}</TableCell>
                          <TableCell align="left">{report.answer_24}</TableCell>
                          <TableCell align="left">{report.answer_25}</TableCell>
                          <TableCell align="left">{report.answer_26}</TableCell>
                          <TableCell align="left">{report.answer_27}</TableCell>
                          <TableCell align="left">{report.answer_28}</TableCell>
                          <TableCell align="left">{report.answer_29}</TableCell>
                          <TableCell align="left">{report.answer_30}</TableCell>
                          <TableCell align="left">{report.answer_31}</TableCell>
                          <TableCell align="left">{report.answer_32}</TableCell>
                          <TableCell align="left">{report.answer_33}</TableCell>
                          <TableCell align="left">{report.answer_34}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <EmptyReportsTable isBadgeShowing={badgeData.length > 0} />
              )}
            </>
          )}

          {tableData.length > 0 && !loading && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 2,
                marginTop: 2,
              }}
            >
              <Typography sx={{ fontSize: "14px" }}>Rows per page</Typography>
              <Select
                id="page-size-select"
                value={perPage}
                onChange={(e) => {
                  setPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                disabled={loading}
                size="small"
                sx={{
                  minWidth: 70,
                  "& .MuiOutlinedInput-notchedOutline": {
                    border: "none",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    border: "none",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    border: "none",
                  },
                }}
              >
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
                <MenuItem value={150}>150</MenuItem>
                <MenuItem value={200}>200</MenuItem>
              </Select>
              {pageCount > 0 && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <Pagination
                    count={pageCount}
                    page={currentPage}
                    onChange={(
                      _event: React.ChangeEvent<unknown>,
                      page: number
                    ) => {
                      selectPage(page);
                    }}
                    boundaryCount={1}
                    siblingCount={1}
                    showFirstButton
                    showLastButton
                  />
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default OneProgramReport;
